/** Form-local content identity only. No storage, requests, result lookup or provider calls. */
export function readPhotoContentIdentity(file: File): Promise<string | null> {
  if (file.size === 0 || file.size > 5 * 1024 * 1024) return Promise.resolve(null);
  return new Promise<string | null>((resolve) => {
    let reader: FileReader;
    try { reader = new FileReader(); } catch { resolve(null); return; }
    let done = false;
    const finish = (identity: string | null) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(identity);
    };
    const timer = setTimeout(() => {
      finish(null);
      if (reader.readyState === FileReader.LOADING) reader.abort();
    }, 10_000);
    reader.onerror = reader.onabort = () => finish(null);
    reader.onload = () => {
      if (done || !reader.result || typeof reader.result === "string") return finish(null);
      try {
        const content = new Uint8Array(reader.result);
        if (content.byteLength !== file.size || !globalThis.crypto?.subtle) return finish(null);
        void globalThis.crypto.subtle.digest("SHA-256", content).then(
          (hash) => finish(`${content.byteLength}:${Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, "0")).join("")}`),
          () => finish(null),
        );
      } catch { finish(null); }
    };
    try { reader.readAsArrayBuffer(file); } catch { finish(null); }
  });
}

export type PhotoSubmission = {
  readonly scope: string;
  readonly epoch: number;
  readonly file: File;
  readonly key: string;
  readonly identity: Promise<string | null>;
};
export type PhotoSelectionDecision = "different" | "same" | "unconfirmed" | "stale" | "pending";

/** Bounded to 32 unresolved operation digests per mounted form/actor/household. */
export class PhotoUnknownSelection {
  private scope = "";
  private epoch = 0;
  private alive = true;
  private pending = false;
  private checking = false;
  private active: PhotoSubmission | null = null;
  private readonly unknown = new Map<string, Promise<string | null>>();

  syncScope(scope: string): void {
    if (scope === this.scope) return;
    this.scope = scope;
    this.invalidate();
  }
  activate(): void { this.alive = true; }
  dispose(): void { this.alive = false; this.invalidate(); }
  private invalidate(): void {
    this.epoch++;
    this.pending = this.checking = false;
    this.active = null;
    this.unknown.clear();
  }
  get isPending(): boolean { return this.pending; }
  get hasUnknown(): boolean { return this.unknown.size > 0; }
  get busy(): boolean { return this.pending || this.checking || this.unknown.size >= 32; }
  cancelSelection(): void { this.epoch++; this.checking = false; }

  begin(file: File, key: string): PhotoSubmission | null {
    if (!this.alive || this.busy) return null;
    const attempt = { scope: this.scope, epoch: ++this.epoch, file, key, identity: readPhotoContentIdentity(file) };
    this.active = attempt;
    this.pending = true;
    return attempt;
  }
  current(attempt: PhotoSubmission): boolean {
    return this.alive && attempt === this.active && attempt.scope === this.scope && attempt.epoch === this.epoch;
  }
  settle(attempt: PhotoSubmission, unknown: boolean): boolean {
    if (!this.current(attempt)) return false;
    this.pending = false;
    if (unknown) this.unknown.set(attempt.key, attempt.identity);
    else this.unknown.delete(attempt.key);
    return true;
  }
  async select(file: File): Promise<PhotoSelectionDecision> {
    if (this.pending) return "pending";
    const epoch = ++this.epoch, scope = this.scope;
    this.checking = true;
    const identities = [...this.unknown.values()];
    const values = identities.length === 0 ? [] : await Promise.all([readPhotoContentIdentity(file), ...identities]);
    if (!this.alive || this.epoch !== epoch || this.scope !== scope) return "stale";
    this.checking = false;
    if (identities.length >= 32 || values.some((value) => value === null)) return "unconfirmed";
    if (values.length > 0 && values.slice(1).includes(values[0]!)) return "same";
    return "different";
  }
}
